import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/absolute-url";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { MAX_LIST_PAGE, parsePageSearch } from "@/lib/search-params";
import { collectionPageSchema, safeJsonLd } from "@/lib/structured-data-core";
import { Header } from "@/components/site/Header";
import { NotFoundPage } from "@/components/site/NotFoundPage";
import { Footer } from "@/components/site/Footer";
import { listBlogPosts, listBlogTaxonomyMeta } from "@/lib/blog.functions";

const metaQO = (slug: string) =>
  queryOptions({
    queryKey: ["blog-tag", slug],
    queryFn: () => listBlogTaxonomyMeta({ data: { kind: "tag", slug } }),
  });

const postsQO = (slug: string, page: number) =>
  queryOptions({
    queryKey: ["blog-posts", { tagSlug: slug, page }],
    queryFn: () => listBlogPosts({ data: { tagSlug: slug, page } }),
  });

export const Route = createFileRoute("/tag/$slug")({
  validateSearch: parsePageSearch,
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ context, params, deps }) => {
    if (deps.page > MAX_LIST_PAGE) throw notFound();
    const meta = await context.queryClient.ensureQueryData(metaQO(params.slug));
    if (!meta) throw notFound();
    const posts = await context.queryClient.ensureQueryData(postsQO(params.slug, deps.page));
    // A page past the last one has nothing to show. Answer 404, not an empty page.
    if (deps.page > 1 && posts.posts.length === 0) throw notFound();
    return meta;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData)
      return {
        meta: [
          { title: "Page Not Found | RESTORE Hair" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    const name = loaderData.name;
    return {
      meta: [
        { title: `#${name} | RESTORE Hair Blog` },
        { name: "description", content: `Posts tagged ${name} on the RESTORE Hair blog.` },
        { name: "robots", content: "noindex, follow" },
      ],
      links: [{ rel: "canonical", href: absoluteUrl(`/tag/${params.slug}/`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: safeJsonLd(collectionPageSchema(name, `Posts tagged ${name} on the RESTORE Hair blog.`, `/tag/${params.slug}`)),
        },
      ],
    };
  },
  component: TagPage,
  notFoundComponent: () => <NotFoundPage />,
  errorComponent: ({ error, reset }) => (
    <div className="p-10 text-center">Something went wrong.<button onClick={reset} className="ml-2 underline">Retry</button></div>
  ),
});

function TagPage() {
  const { slug } = Route.useParams();
  const { page } = Route.useSearch();
  const currentPage = page ?? 1;
  const { data: meta } = useSuspenseQuery(metaQO(slug));
  const { data } = useSuspenseQuery(postsQO(slug, currentPage));
  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <>
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-12 lg:py-16">
        <header className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">Tag</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-foreground lg:text-4xl">#{meta?.name}</h1>
        </header>
        {data.posts.length === 0 ? (
          <p className="text-muted-foreground">No posts with this tag yet.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {data.posts.map((p) => (
              <li key={p.id} className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-lg">
                <Link to="/$slug/" params={{ slug: p.slug }} className="flex h-full flex-col">
                  {p.featured_image_url ? (
                    <div className="aspect-[16/9] overflow-hidden bg-muted">
                      <img src={p.featured_image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    </div>
                  ) : null}
                  <div className="flex flex-1 flex-col gap-2 p-5">
                    <h2 className="text-lg font-semibold leading-snug text-foreground group-hover:text-primary">{p.title}</h2>
                    {p.excerpt ? <p className="line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p> : null}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {totalPages > 1 ? (
          <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Pagination">
            {currentPage > 1 ? <Link to="/tag/$slug/" params={{ slug }} search={{ page: currentPage > 2 ? currentPage - 1 : undefined }} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">← Newer</Link> : null}
            <span className="px-3 py-2 text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
            {currentPage < totalPages ? <Link to="/tag/$slug/" params={{ slug }} search={{ page: currentPage + 1 }} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">Older →</Link> : null}
          </nav>
        ) : null}
      </main>
      <Footer />
    </>
  );
}